import { BottomNavigation, Box, Button, Typography } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { useProduct } from '../../store/product.store';
import { useCart } from '../../store/cart.store';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useModals } from '../../provider/ModalProvider';
import { useDebounce } from 'use-debounce';

const FooterItemAddToCart = () => {
  const { selectedProduct } = useProduct();
  const {
    getCartItem,
    increaseCart,
    decreaseCart,
    addToCart,
    changeQuantity,
    deleteFromCart,
  } = useCart();
  const cart = getCartItem(selectedProduct!);
  const { notifyAlert } = useModals();
  const [debouncedQty] = useDebounce(cart?.quantity, 1000);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when product is added
  useEffect(() => {
    if (cart && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [cart]);

  const handleIncrease = () => {
    if (!cart && selectedProduct) {
      addToCart(selectedProduct);
    } else {
      increaseCart(selectedProduct!.sku);
    }
  };

  useEffect(() => {
    if (
      selectedProduct?.minimumQuantity &&
      selectedProduct.minimumQuantity > Number(debouncedQty) &&
      debouncedQty !== 0 &&
      cart
    ) {
      notifyAlert(selectedProduct);
      deleteFromCart(selectedProduct.sku);
    }
  }, [debouncedQty]);

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!cart || cart.quantity === 0) {
      addToCart(selectedProduct!);
      // early return; effect will re-focus
      return;
    }
    e.currentTarget.select();
  };

  return (
    <BottomNavigation
      showLabels
      sx={{
        height: '120px',
        padding: '10px 0 40px',
        background: 'var(--white-base-cards, #FFF)',
        boxShadow: '0px -2px 7px rgba(0, 0, 0, 0.10)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        '@supports (color: display-p3)': {
          background: 'var(--white-base-cards, color(display-p3 1 1 1))',
          boxShadow: '0px -2px 7px color(display-p3 0 0 0 / 0.10)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '10px',
          bgcolor: '#F5F7FA',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: '10px',
        }}
      >
        <Button onClick={() => decreaseCart(selectedProduct?.sku!)}>
          <RemoveIcon sx={{ fontSize: '30px' }} />
        </Button>
        <Box sx={{ display: 'flex', gap: '10px' }}>
          {selectedProduct?.isEditable ? (
            <input
              ref={inputRef}
              type="number"
              value={cart?.quantity ?? '0'}
              onFocus={handleInputFocus}
              onChange={(e) =>
                changeQuantity(
                  selectedProduct!.sku,
                  +e.target.value,
                  notifyAlert
                )
              }
              onMouseUp={(e) => e.preventDefault()}
              style={{
                width: '40px',
                textAlign: 'center',
                border: 'none',
                backgroundColor: '#F5F7FA',
                fontSize: '16px',
                color: '#154D72',
              }}
            />
          ) : (
            <Typography
              color="#154D72"
              fontWeight={700}
              lineHeight="18px"
              letterSpacing="0.3px"
            >
              {cart?.quantity ?? '0'}
            </Typography>
          )}

          <Typography
            color="#154D72"
            fontWeight={700}
            lineHeight="18px"
            letterSpacing="0.3px"
          >
            {selectedProduct?.packName}
          </Typography>
        </Box>

        <Button onClick={handleIncrease}>
          <AddIcon sx={{ fontSize: '30px' }} />
        </Button>
      </Box>
      <Typography
        color="#154D72"
        fontWeight={700}
        lineHeight="18px"
        letterSpacing="0.3px"
      >
        סה"כ {(cart?.quantity ?? 0) * (selectedProduct?.packQuantity ?? 1)}{' '}
        {selectedProduct?.packUnit ?? `יח'`}
      </Typography>
    </BottomNavigation>
  );
};

export default FooterItemAddToCart;